import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ClubLogo } from '../lib/club';
import { Button, Card, CardBody, Field, Input, LoadingBlock } from '../components/ui';

export function LoginPage(): JSX.Element {
  const { user, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [initialResetToken] = useState(() => {
    const token = new URLSearchParams(window.location.hash.slice(1)).get('resetToken') ?? '';
    if (token) window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return token;
  });
  const [resetMode, setResetMode] = useState(Boolean(initialResetToken));
  const [resetInfo, setResetInfo] = useState<string | null>(null);
  const [resetToken, setResetToken] = useState(initialResetToken);
  const [newPassword, setNewPassword] = useState('');
  const [loginInfo, setLoginInfo] = useState<string | null>(null);

  if (isLoading) return <LoadingBlock label="Anmeldung wird geprüft …" />;
  if (user && !resetMode) {
    const target = (location.state as { from?: string } | null)?.from ?? '/heute';
    return <Navigate to={target} replace />;
  }

  const handleLogin = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setLoginInfo(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate('/heute', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Anmeldung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleResetRequest = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<{ message: string; devToken?: string }>(
        '/api/auth/password-reset/request',
        { email },
      );
      setResetInfo(result.message);
      if (result.devToken) setResetToken(result.devToken);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Anfrage fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleResetConfirm = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post('/api/auth/password-reset/confirm', {
        token: resetToken,
        password: newPassword,
      });
      setResetMode(false);
      setResetInfo(null);
      setResetToken('');
      setPassword(newPassword);
      setNewPassword('');
      setLoginInfo('Passwort gesetzt – du kannst dich jetzt anmelden');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Zurücksetzen fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center">
            <ClubLogo size={56} className="rounded-2xl" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">UK Info</h1>
          <p className="text-sm text-slate-500">Die zentrale Vereinsplattform</p>
        </div>

        <Card>
          <CardBody>
            {!resetMode && (
              <form onSubmit={handleLogin} className="space-y-4">
                <Field label="E-Mail" required>
                  {(id) => (
                    <Input
                      id={id}
                      type="email"
                      autoComplete="username"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@verein.at"
                    />
                  )}
                </Field>
                <Field label="Passwort" required>
                  {(id) => (
                    <Input
                      id={id}
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  )}
                </Field>

                {loginInfo && (
                  <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{loginInfo}</p>
                )}
                {error && (
                  <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
                )}

                <Button type="submit" className="w-full" size="lg" loading={busy}>
                  Anmelden
                </Button>

                <button
                  type="button"
                  onClick={() => {
                    setResetMode(true);
                    setError(null);
                  }}
                  className="w-full text-center text-sm text-brand-700 hover:underline"
                >
                  Passwort vergessen?
                </button>
              </form>
            )}

            {resetMode && (
              <div className="space-y-4">
                {!resetToken && (
                  <form onSubmit={handleResetRequest} className="space-y-4">
                    <Field label="E-Mail" required>
                      {(id) => (
                        <Input
                          id={id}
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      )}
                    </Field>
                    {resetInfo && (
                      <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                        {resetInfo}
                      </p>
                    )}
                    <Button type="submit" className="w-full" loading={busy}>
                      Link anfordern
                    </Button>
                  </form>
                )}

                {resetToken && (
                  <form onSubmit={handleResetConfirm} className="space-y-4">
                    <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      Lege dein neues Passwort fest. Der Link ist nur begrenzt gültig.
                    </p>
                    <Field label="Rücksetz-Code" required>
                      {(id) => (
                        <Input
                          id={id}
                          value={resetToken}
                          onChange={(e) => setResetToken(e.target.value)}
                        />
                      )}
                    </Field>
                    <Field label="Neues Passwort" required hint="Mindestens 8 Zeichen">
                      {(id) => (
                        <Input
                          id={id}
                          type="password"
                          required
                          minLength={8}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                      )}
                    </Field>
                    <Button type="submit" className="w-full" loading={busy}>
                      Passwort setzen
                    </Button>
                  </form>
                )}

                {error && (
                  <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setResetMode(false);
                    setResetInfo(null);
                    setResetToken('');
                    setError(null);
                  }}
                  className="w-full text-center text-sm text-slate-500 hover:underline"
                >
                  Zurück zur Anmeldung
                </button>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
